/**
 * Rutas localizadas (RF-LANG-09).
 *
 * La URL lleva el idioma por delante y los segmentos estan traducidos, de modo
 * que `/es/competiciones` y `/en/competitions` son la misma pantalla. Asi los
 * enlaces compartidos y el posicionamiento respetan la version correcta.
 */

import type { Language } from '@/types/domain';

export const ROUTE_SEGMENTS = {
  competitions: { es: 'competiciones', en: 'competitions' },
  forum: { es: 'foro', en: 'forum' },
  rankings: { es: 'rankings', en: 'rankings' },
  athletes: { es: 'atletas', en: 'athletes' },
  profile: { es: 'perfil', en: 'profile' },
  settings: { es: 'ajustes', en: 'settings' },
  admin: { es: 'admin', en: 'admin' },
  auth: { es: 'acceso', en: 'auth' },
  login: { es: 'entrar', en: 'login' },
  signup: { es: 'registro', en: 'signup' },
  recover: { es: 'recuperar', en: 'recover' },
  verify: { es: 'verificar', en: 'verify' },
  onboarding: { es: 'bienvenida', en: 'welcome' },
  language: { es: 'idioma', en: 'language' },
  tutorial: { es: 'tutorial', en: 'tutorial' },
  submit: { es: 'enviar', en: 'submit' },
  marks: { es: 'marcas', en: 'marks' },
  terms: { es: 'terminos', en: 'terms' },
  privacy: { es: 'privacidad', en: 'privacy' },
  about: { es: 'quien-soy', en: 'about' },
  global: { es: 'global', en: 'global' },
  local: { es: 'local', en: 'local' },
} as const;

export type RouteKey = keyof typeof ROUTE_SEGMENTS;

/** Segmento traducido de una ruta. */
export function seg(key: RouteKey, language: Language): string {
  return ROUTE_SEGMENTS[key][language];
}

/** Construye una ruta absoluta con el prefijo de idioma. */
export function path(language: Language, ...parts: (RouteKey | string)[]): string {
  const segments = parts.map((part) =>
    part in ROUTE_SEGMENTS ? seg(part as RouteKey, language) : String(part),
  );
  return `/${[language, ...segments].filter(Boolean).join('/')}`;
}

/** Rutas de uso frecuente, para no repetir `path(...)` por todas partes. */
export const routes = {
  home: (l: Language) => path(l),
  competitions: (l: Language) => path(l, 'competitions'),
  competition: (l: Language, slug: string) => path(l, 'competitions', slug),
  submit: (l: Language, slug: string) => path(l, 'competitions', slug, 'submit'),
  submission: (l: Language, slug: string, id: string) =>
    path(l, 'competitions', slug, 'marks', id),
  forum: (l: Language) => path(l, 'forum'),
  rankings: (l: Language) => path(l, 'rankings', 'global'),
  rankingsGlobal: (l: Language) => path(l, 'rankings', 'global'),
  rankingsLocal: (l: Language) => path(l, 'rankings', 'local'),
  athlete: (l: Language, alias: string) => path(l, 'athletes', encodeURIComponent(alias)),
  profile: (l: Language) => path(l, 'profile'),
  settings: (l: Language) => path(l, 'settings'),
  admin: (l: Language, sub?: string) => (sub ? path(l, 'admin', sub) : path(l, 'admin')),
  login: (l: Language) => path(l, 'auth', 'login'),
  signup: (l: Language) => path(l, 'auth', 'signup'),
  recover: (l: Language) => path(l, 'auth', 'recover'),
  verify: (l: Language) => path(l, 'auth', 'verify'),
  onboardingLanguage: (l: Language) => path(l, 'onboarding', 'language'),
  onboardingProfile: (l: Language) => path(l, 'onboarding'),
  onboardingTutorial: (l: Language) => path(l, 'onboarding', 'tutorial'),
  terms: (l: Language) => path(l, 'terms'),
  privacy: (l: Language) => path(l, 'privacy'),
  about: (l: Language) => path(l, 'about'),
};

/**
 * Traduce una ruta completa de un idioma a otro conservando la pantalla.
 * Los segmentos que no son palabras de ruta —el alias de un atleta, el slug
 * de una competicion— pasan intactos.
 */
export function translatePath(pathname: string, to: Language): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return `/${to}`;

  const [, ...rest] = segments; // el primer segmento es el idioma actual
  const translated = rest.map((segment) => {
    const decoded = decodeURIComponent(segment);
    for (const value of Object.values(ROUTE_SEGMENTS)) {
      if (value.es === decoded || value.en === decoded) return value[to];
    }
    return segment;
  });

  return `/${[to, ...translated].join('/')}`;
}

/** Subsecciones del panel de administracion. */
/**
 * Secciones del panel. De ocho a cinco: programacion agrupa competiciones y
 * temporadas, catalogo agrupa ejercicios y categorias, y actividad agrupa
 * metricas y auditoria. Las rutas viejas siguen redirigiendo (AdminLayout).
 */
export const ADMIN_SECTIONS = [
  'queue',
  'schedule',
  'catalog',
  'users',
  'activity',
] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];
